import React, { useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Users,
  FileText,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  Download,
  Activity,
  BarChart3,
  PieChart,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Badge } from '@components/common/Badge';
import { Button } from '@components/common/Button';
import { mockApplicants } from '@mocks/applicants';
import { mockApplications } from '@mocks/licenses';
import { mockAppointments } from '@mocks/appointments';
import { mockCenters } from '@mocks/bookings';

export const Analytics: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [selectedCenter, setSelectedCenter] = useState<number | 'all'>('all');

  // Filter data by selected center
  const filteredAppointments = selectedCenter === 'all'
    ? mockAppointments
    : mockAppointments.filter((apt) => apt.center_id === selectedCenter);

  // Get applicant IDs who have appointments at selected center
  const applicantIdsInCenter = selectedCenter === 'all'
    ? mockApplicants.map((a) => a.id)
    : [...new Set(filteredAppointments.map((apt) => apt.applicant_id))];

  const filteredApplications = selectedCenter === 'all'
    ? mockApplications
    : mockApplications.filter((app) => applicantIdsInCenter.includes(app.applicant_id));

  const filteredApplicants = selectedCenter === 'all'
    ? mockApplicants
    : mockApplicants.filter((a) => applicantIdsInCenter.includes(a.id));

  // Calculate statistics
  const stats = {
    totalApplicants: filteredApplicants.length,
    activeApplicants: filteredApplicants.filter((a) => a.account_status === 'ACTIVE').length,
    suspendedApplicants: filteredApplicants.filter((a) => a.account_status === 'SUSPENDED').length,
    
    totalApplications: filteredApplications.length,
    approvedApplications: filteredApplications.filter((a) => a.status === 'APPROVED').length,
    pendingApplications: filteredApplications.filter((a) => ['SUBMITTED', 'DOCUMENT_REVIEW', 'BIOMETRIC_PENDING', 'MEDICAL_REVIEW'].includes(a.status)).length,
    rejectedApplications: filteredApplications.filter((a) => a.status === 'REJECTED').length,
    
    totalAppointments: filteredAppointments.length,
    scheduledAppointments: filteredAppointments.filter((a) => a.status === 'SCHEDULED').length,
    completedAppointments: filteredAppointments.filter((a) => a.status === 'COMPLETED').length,
    cancelledAppointments: filteredAppointments.filter((a) => a.status === 'CANCELLED').length,
    
    totalCenters: mockCenters.length,
    activeCenters: mockCenters.filter((c) => c.is_active).length,
  };

  // Calculate rates and percentages
  const approvalRate = Math.round((stats.approvedApplications / stats.totalApplications) * 100);
  const completionRate = Math.round((stats.completedAppointments / stats.totalAppointments) * 100);
  const activeApplicantRate = Math.round((stats.activeApplicants / stats.totalApplicants) * 100);

  // Mock trends (in real app, compare with previous period)
  const trends = {
    applicants: { value: 12, isUp: true },
    applications: { value: 8, isUp: true },
    appointments: { value: 5, isUp: false },
  };

  const handleExport = () => {
    console.log('✅ Mock: Export analytics report');
    alert('Would download analytics report as PDF/Excel');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-ncaa-dark">Analytics Dashboard</h1>
            <p className="text-ncaa-medium mt-1">
              {selectedCenter === 'all'
                ? 'System-wide performance metrics and insights'
                : `Performance metrics for ${mockCenters.find((c) => c.id === selectedCenter)?.name}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Center Filter */}
            <select
              value={selectedCenter}
              onChange={(e) => setSelectedCenter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
            >
              <option value="all">All Centers</option>
              {mockCenters.map((center) => (
                <option key={center.id} value={center.id}>
                  {center.name}
                </option>
              ))}
            </select>
            {/* Time Range Selector */}
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
            <Button onClick={handleExport} variant="outline" icon={<Download className="h-4 w-4" />}>
              Export Report
            </Button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <Card>
          <div className="flex items-center justify-between mb-2">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex items-center gap-1 text-sm">
              {trends.applicants.isUp ? (
                <TrendingUp className="h-4 w-4 text-green-600" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-600" />
              )}
              <span className={trends.applicants.isUp ? 'text-green-600' : 'text-red-600'}>
                {trends.applicants.value}%
              </span>
            </div>
          </div>
          <h3 className="text-sm font-medium text-gray-600">Total Applicants</h3>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.totalApplicants}</p>
          <p className="text-xs text-gray-500 mt-1">{stats.activeApplicants} active</p>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-2">
            <div className="p-3 bg-green-100 rounded-lg">
              <FileText className="h-6 w-6 text-green-600" />
            </div>
            <div className="flex items-center gap-1 text-sm">
              {trends.applications.isUp ? (
                <TrendingUp className="h-4 w-4 text-green-600" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-600" />
              )}
              <span className={trends.applications.isUp ? 'text-green-600' : 'text-red-600'}>
                {trends.applications.value}%
              </span>
            </div>
          </div>
          <h3 className="text-sm font-medium text-gray-600">Applications</h3>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.totalApplications}</p>
          <p className="text-xs text-gray-500 mt-1">{approvalRate}% approval rate</p>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-2">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Calendar className="h-6 w-6 text-purple-600" />
            </div>
            <div className="flex items-center gap-1 text-sm">
              {trends.appointments.isUp ? (
                <TrendingUp className="h-4 w-4 text-green-600" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-600" />
              )}
              <span className={trends.appointments.isUp ? 'text-green-600' : 'text-red-600'}>
                {trends.appointments.value}%
              </span>
            </div>
          </div>
          <h3 className="text-sm font-medium text-gray-600">Appointments</h3>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.totalAppointments}</p>
          <p className="text-xs text-gray-500 mt-1">{completionRate}% completion rate</p>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-2">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Activity className="h-6 w-6 text-orange-600" />
            </div>
          </div>
          <h3 className="text-sm font-medium text-gray-600">Testing Centers</h3>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.totalCenters}</p>
          <p className="text-xs text-gray-500 mt-1">{stats.activeCenters} active centers</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Application Status Breakdown */}
        <Card title="Application Status" icon={<BarChart3 className="h-5 w-5" />}>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">Approved</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{stats.approvedApplications}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full"
                  style={{ width: `${(stats.approvedApplications / stats.totalApplications) * 100}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm font-medium text-gray-700">Pending</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{stats.pendingApplications}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow-600 h-2 rounded-full"
                  style={{ width: `${(stats.pendingApplications / stats.totalApplications) * 100}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium text-gray-700">Rejected</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{stats.rejectedApplications}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-600 h-2 rounded-full"
                  style={{ width: `${(stats.rejectedApplications / stats.totalApplications) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Appointment Status */}
        <Card title="Appointment Status" icon={<PieChart className="h-5 w-5" />}>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-medium text-gray-700">Scheduled</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{stats.scheduledAppointments}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full"
                  style={{ width: `${(stats.scheduledAppointments / stats.totalAppointments) * 100}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">Completed</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{stats.completedAppointments}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full"
                  style={{ width: `${(stats.completedAppointments / stats.totalAppointments) * 100}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium text-gray-700">Cancelled</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{stats.cancelledAppointments}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-600 h-2 rounded-full"
                  style={{ width: `${(stats.cancelledAppointments / stats.totalAppointments) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance Metrics */}
        <Card title="Performance Metrics">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-200">
              <span className="text-sm text-gray-600">Approval Rate</span>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">{approvalRate}%</p>
                </div>
                <Badge variant="success" size="sm">Good</Badge>
              </div>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-200">
              <span className="text-sm text-gray-600">Completion Rate</span>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">{completionRate}%</p>
                </div>
                <Badge variant="success" size="sm">Good</Badge>
              </div>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm text-gray-600">Active Users</span>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">{activeApplicantRate}%</p>
                </div>
                <Badge variant="success" size="sm">Excellent</Badge>
              </div>
            </div>
          </div>
        </Card>

        {/* Top Centers */}
        <Card title="Testing Centers">
          <div className="space-y-3">
            {mockCenters.slice(0, 5).map((center) => (
              <div key={center.id} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0">
                <div>
                  <p className="text-sm font-medium text-gray-900">{center.name}</p>
                  <p className="text-xs text-gray-500">{center.city}</p>
                </div>
                <Badge variant={center.is_active ? 'success' : 'default'} size="sm">
                  {center.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            ))}
          </div>
        </Card>

        {/* Quick Stats */}
        <Card title="Quick Stats">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-200">
              <span className="text-sm text-gray-600">Avg. Processing Time</span>
              <p className="text-lg font-bold text-gray-900">3.5 days</p>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-200">
              <span className="text-sm text-gray-600">Pending Reviews</span>
              <p className="text-lg font-bold text-gray-900">{stats.pendingApplications}</p>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm text-gray-600">System Uptime</span>
              <div className="text-right">
                <p className="text-lg font-bold text-green-600">99.9%</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
